import { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components';

import { AssetCategory } from '@/entities/assets';
import { BeneficiaryGroupI18n } from '@/entities/beneficiary';
import { ContactTypeI18n } from '@/entities/contact';
import { PidType } from '@/entities/personalIdentifiers';
import { useActions } from '@/entities/personalIdentifiers/hooks/useActions';
import { PrivateDocumentCategory } from '@/entities/privateDocuments';
import { Country } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { PickDtoFilter } from '@/shared/lib/forms/form-wrapper.types';
import { BeneficiaryDto, ContactDto, PrivateDocumentDto, useSJDatabase } from '@/shared/lib/sj-orm';
import { AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { CollectionName } from '@/shared/lib/sj-orm/types';
import { useProfileStore } from '@/shared/store/profile.store';
import { dates } from '@/shared/utils/dates';
import { numbers } from '@/shared/utils/numbers';
import { compareStrings } from '@/shared/utils/strings';

import { AllowedCollectionsSelectDataMap, AllowedCollectionsSelectDataNames } from './types';

type UseSelectDataProps<C extends AllowedCollectionsSelectDataNames> = {
  colName: C;
  excludeIds?: DtoId[];
  filter?: PickDtoFilter;
};

export const useSelectData = <C extends AllowedCollectionsSelectDataNames>({
  colName,
  excludeIds,
  filter,
}: UseSelectDataProps<C>) => {
  const { t } = useTranslation('widgets/modals', {
    keyPrefix: 'selectCollectionItem',
  });
  const sjDb = useSJDatabase();
  const { resolveBaseCurrency, isAssistant } = useProfileStore();
  const { getAssociatedBeneficiary } = useActions();

  type Dto = AllowedCollectionsSelectDataMap[C];

  const hideForCapitalFounderFilter = useCallback(
    (dto: Dto) =>
      isAssistant ||
      (!isAssistant && !(dto as { isCreatedByAssistant?: boolean })?.isCreatedByAssistant),
    [isAssistant],
  );

  const filterItems = useCallback(
    (items: Dto[]) => {
      if (excludeIds || filter) {
        return items.filter((item) => {
          return (
            (!filter || filter(item, null, '')) && (!excludeIds || !excludeIds.includes(item.id))
          );
        });
      } else {
        return items;
      }
    },
    [excludeIds, filter],
  );

  return useMemo<{
    items: Dto[];
    columns: ColumnsType<Dto>;
  }>(() => {
    //#region ASSETS
    if (colName === CollectionName.ASSETS) {
      const allItems = sjDb.collections.assets?.getAll() ?? [];
      const items = filterItems(allItems as Dto[]).filter(hideForCapitalFounderFilter);

      const columns: ColumnsType<AssetDto> = [
        {
          key: 'nickname',
          title: t('assets.title'),
          render: (item) => <div className="lineclamp2">{item.nickname ?? item.nickName}</div>,
        },
        {
          key: 'category',
          title: t('assets.category'),
          render: (item) => <AssetCategory category={item.category} />,
        },
        {
          key: 'country',
          title: t('assets.country'),
          width: 200,
          render: (item) => <Country code={item.country} />,
        },
        {
          key: 'value',
          title: t('assets.value', {
            currency: resolveBaseCurrency(),
          }),
          width: 200,
          render: (item) =>
            item.purchaseValue ? numbers.formatPrice(item.purchaseValue.value) : '-',
        },
      ];

      return { items, columns: columns as ColumnsType<Dto> };
    }

    //#region PERSONAL_IDENTIFIERS_DOCUMENTS
    if (colName === CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS) {
      const allItems = sjDb.collections.personalIdentifiersDocuments?.getAll() ?? [];
      const items = filterItems(allItems as Dto[]).filter(hideForCapitalFounderFilter);

      const columns: ColumnsType<PersonalIdentifierDocumentDto> = [
        {
          key: 'nickname',
          title: t('pids.title'),
          render: (item) => (
            <div>
              <div className="lineclamp2">{item.name || '-'}</div>
              <DateContainer>{item.endDate ? dates.formatDate(item.endDate) : '-'}</DateContainer>
            </div>
          ),
        },
        {
          key: 'type',
          title: t('pids.type'),
          render: (item) => <PidType type={item.personalIdentifierType} />,
        },
        {
          key: 'associatedWith',
          title: t('pids.associatedWith'),
          render: (_, record) => {
            const { associatedWith } = getAssociatedBeneficiary(record);
            return associatedWith;
          },
        },
        {
          key: 'country',
          title: t('pids.country'),
          width: 200,
          render: (item) => <Country code={item.country} />,
        },
      ];

      return { items, columns: columns as ColumnsType<Dto> };
    }

    //#region PRIVATE_DOCUMENTS
    if (colName === CollectionName.PRIVATE_DOCUMENTS) {
      const allItems = sjDb.collections.privateDocuments?.getAll() ?? [];
      const items = filterItems(allItems as Dto[]).filter(hideForCapitalFounderFilter);

      const columns: ColumnsType<PrivateDocumentDto> = [
        {
          key: 'name',
          title: t('docs.title'),
          render: (item) => <div className="lineclamp2">{item.name ?? '-'}</div>,
          sorter: (a, b) => compareStrings(a.name, b.name),
        },
        {
          key: 'category',
          title: t('docs.category'),
          render: (item) => <PrivateDocumentCategory category={item.privateCategory} />,
          sorter: (a, b) => compareStrings(a.privateCategory, b.privateCategory),
        },
        {
          key: 'country',
          title: t('docs.country'),
          width: 200,
          render: (item) => <Country code={item.country} />,
          sorter: (a, b) => compareStrings(a.country, b.country),
        },
      ];

      return { items, columns: columns as ColumnsType<Dto> };
    }

    // #region CONTACTS
    if (colName === CollectionName.CONTACTS) {
      const allItems = sjDb.collections.contacts?.getAll() ?? [];
      const items = filterItems(allItems as Dto[]);

      const columns: ColumnsType<ContactDto> = [
        {
          key: 'nickname',
          title: t('contacts.title'),
          render: (item) => <div className="lineclamp2">{item.nickname}</div>,
        },
        {
          key: 'type',
          title: t('contacts.type'),
          render: (item) => <ContactTypeI18n type={item.type} />,
        },
        {
          key: 'selectedCountry',
          title: t('contacts.country'),
          render: (item) => <Country code={item.selectedCountry} />,
        },
      ];

      return { items, columns: columns as ColumnsType<Dto> };
    }

    // #region BENEFICIARIES
    if (colName === CollectionName.BENEFICIARIES) {
      const allItems = sjDb.collections.beneficiaries?.getAll() ?? [];
      const items = [{ id: 'me', nickName: 'Me' }, ...filterItems(allItems as Dto[])] as Dto[];

      const columns: ColumnsType<BeneficiaryDto> = [
        {
          key: 'nickName',
          title: t('beneficiaries.title'),
          render: (item) => <div className="lineclamp2">{item.nickName}</div>,
        },
        {
          key: 'group',
          title: t('beneficiaries.group'),
          render: (item) =>
            item.group ? <BeneficiaryGroupI18n showIcon group={item.group} /> : '-',
        },
        {
          key: 'email',
          title: t('beneficiaries.email'),
          render: (item) => (item.email ? item.email : '-'),
        },
      ];

      return { items, columns: columns as ColumnsType<Dto> };
    }

    return { items: [], columns: [] };
  }, [colName, sjDb, filterItems, t, hideForCapitalFounderFilter, resolveBaseCurrency]);
};

const DateContainer = styled.div`
  color: ${COLORS.systemDark200};
  font-size: 12px;
  line-height: 20px;
`;
